// Session slides for the Cohort workspace.
// Admin can toggle visibility and scope per slide via the UI.
// Slides default to hidden from learners until an admin switches them on.
//
// File naming: {track}-{nn}-{slug}.pptx  →  frontend/public/slides/raw_slides/
// PDF output:  {track}-{nn}-{slug}.pdf   →  frontend/public/slides/
//
// track slugs:
//   null                      = cross-track (all cohorts)
//   'data-analyst-job-ready'
//   'sql-job-ready'
//   'python-job-ready'
//   'azure-data-engineer'

import API_CONCEPT_CLINICS from './api_concept_clinics';

const SESSION_SLIDES = [
  ...API_CONCEPT_CLINICS,

  // ── Cross-track (Week 1 — every cohort) ───────────────────────────────────
  {
    id: 'ct-01-understanding-data',
    title: 'CT 01 · Understanding Data',
    topics: ['What is data?', 'Data lifecycle', 'Data name, formats, types & structure'],
    resources: [
      {
        label: 'Understanding data take-home',
        ctaLabel: 'Open exercise',
        href: '/data/guides/ct-01-understanding-data-take-home.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'ct-01-understanding-data.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: '2026-06-26',
  },
  {
    id: 'ct-02-data-quality',
    title: 'CT 02 · Data Quality & Cleaning',
    topics: ['The 6 quality dimensions', 'Cleaning loop', 'Nulls, duplicates & outliers'],
    resources: [
      {
        label: 'Data quality take-home',
        ctaLabel: 'Open exercise',
        href: '/data/guides/ct-02-data-quality-cleaning-take-home.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'ct-02-data-quality.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'dq-math-01-data-quality-issues-foundation',
    title: 'DQ Math 01 · Data Quality Issues Foundation',
    topics: ['6 quality dimensions', 'Representativeness', 'Issue scope', 'DQ reasoning'],
    resources: [
      {
        label: 'Data quality issues foundation note',
        ctaLabel: 'Open lesson note',
        href: '/data/guides/dq-math-01-data-quality-issues-foundation.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'dq-math-01-data-quality-issues-foundation.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: '2026-07-16',
    kind: 'note',
  },
  {
    id: 'dq-math-02-missing-data',
    title: 'DQ Math 02 · Missing Data',
    topics: ['Missingness rate', 'MCAR / MAR / MNAR', 'Denominators', 'Imputation risk'],
    resources: [
      {
        label: 'Missing data deep clinic note',
        ctaLabel: 'Open lesson note',
        href: '/data/guides/dq-math-02-missing-data.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Missingness sample table bank',
        ctaLabel: 'Open sample tables',
        href: '/data/guides/dq-math-02-missing-data-sample-tables.md',
        modal: 'markdown',
      },
    ],
    file: 'dq-math-02-missing-data.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: '2026-07-16',
    kind: 'note',
  },
  {
    id: 'dq-math-03-uniqueness-duplicates',
    title: 'DQ Math 03 - Uniqueness & Duplicates',
    topics: ['Duplicate rate', 'Table grain', 'Survivorship rules', 'Join fan-out'],
    resources: [
      {
        label: 'Uniqueness and duplicates clinic note',
        ctaLabel: 'Open lesson note',
        href: '/data/guides/dq-math-03-uniqueness-duplicates.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Duplicate sample table bank',
        ctaLabel: 'Open sample tables',
        href: '/data/guides/dq-math-03-uniqueness-duplicates-sample-tables.md',
        modal: 'markdown',
      },
    ],
    file: 'dq-math-03-uniqueness-duplicates.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: '2026-07-20',
    kind: 'note',
  },
  {
    id: 'dq-math-04-outliers-accuracy',
    title: 'DQ Math 04 - Outliers & Accuracy',
    topics: ['Statistical outliers', 'Legitimate extremes', 'Unit and decimal errors', 'Treatment rules'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/dq-math-04-outliers-accuracy.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Examples and learner practice',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/dq-math-04-outliers-accuracy-sample-tables.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: null,
    cohort: 1,
    active: true,
    date: '2026-07-23',
    kind: 'note',
  },
  {
    id: 'dq-math-05-invalid-values-rule-violations',
    title: 'DQ Math 05 - Invalid Values & Rule Violations',
    topics: ['Validity rules', 'Single-value checks', 'Cross-field checks', 'Referential checks'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/dq-math-05-invalid-values-rule-violations.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples before learner practice',
        ctaLabel: 'Open solved examples',
        href: '/data/guides/dq-math-05-invalid-values-rule-violations-solved-examples.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: null,
    cohort: 1,
    active: true,
    date: '2026-07-27',
    kind: 'note',
  },
  {
    id: 'ct-03-understanding-data-for-analysis',
    title: 'CT 03 · Understanding Data for Analysis',
    topics: ['QCC Way', 'Role → Value Type → Scale → Action', 'Dataset classification'],
    resources: [
      {
        label: 'QCC retail orders practice workbook',
        ctaLabel: 'Open workbook',
        href: '/data/datasets/qcc-retail-orders-qcc-practice-workbook.xlsx',
        cardCta: true,
      },
      {
        label: 'QCC decision tree route table',
        href: '/data/datasets/qcc-decision-tree.csv',
      },
      {
        label: 'QCC visual decision tree',
        href: '/images/qcc-decision-tree-flow.svg',
        modal: 'image',
      },
      {
        label: 'Instructor answer key',
        href: '/data/datasets/qcc-retail-orders-answer-key.csv',
        locked: true,
      },
    ],
    file: 'ct-03-understanding-data-for-analysis.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'ct-04-analytics-workflow',
    title: 'CT 04 · The Analytics Workflow',
    topics: ['From question to decision', 'The 7-stage loop', 'Where each track fits'],
    resources: [
      {
        label: 'Workflow take-home',
        ctaLabel: 'Open exercise',
        href: '/data/guides/ct-04-analytics-workflow-take-home.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'ct-04-analytics-workflow.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'ct-05-communicating-with-data',
    title: 'CT 05 · Communicating with Data',
    topics: ['Chart selection guide', 'The lie factor', 'Data storytelling (SCQA)'],
    resources: [
      {
        label: 'Chart decision guide',
        ctaLabel: 'Open guide',
        href: '/data/guides/chart-decision-guide.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Chart review checklist',
        href: '/data/guides/chart-decision-guide.md#quick-review-checklist',
        modal: 'markdown',
      },
    ],
    file: 'ct-05-communicating-with-data.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'ct-06-career-pathways',
    title: 'CT 06 · Career Pathways in Data',
    topics: ['Data roles landscape', 'Entry-level reality', 'How tracks map to careers'],
    resources: [
      {
        label: 'Career pathway take-home',
        ctaLabel: 'Open exercise',
        href: '/data/guides/ct-06-career-pathways-take-home.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'ct-06-career-pathways.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'ct-07-types-of-analysis',
    title: 'CT 07 · Types of Analysis',
    topics: ['Descriptive', 'Diagnostic', 'Predictive', 'Prescriptive'],
    resources: [
      {
        label: 'Types of analysis take-home',
        ctaLabel: 'Open exercise',
        href: '/data/guides/ct-07-types-of-analysis-take-home.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'ct-07-types-of-analysis.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'excel-analyst-challenge-5-scenarios',
    title: 'Excel Clinic · Real-Life Analyst Scenarios',
    topics: ['Excel in real life', 'Lookups', 'IF logic', 'PivotTables', 'Decision-ready analysis'],
    resources: [
      {
        label: 'Excel analyst scenario clinic',
        ctaLabel: 'Open clinic',
        href: '/data/guides/excel-analyst-challenge-5-scenarios.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Download workbook',
        href: '/data/datasets/excel-analyst-challenge-5-scenarios.xlsx',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: null,
    kind: 'note',
  },
  {
    id: 'ct-08-data-tables-fields-relationships',
    title: 'CT 08 · Data Tables, Fields & Relationships',
    topics: ['Grain of a table', 'Data models', 'Primary & foreign keys', 'One-to-many relationships'],
    resources: [
      {
        label: 'Table Detective exercise + graded practice',
        ctaLabel: 'Open exercise',
        href: '/data/guides/table-detective-exercise.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Northstar retail export',
        href: '/data/datasets/table-detective-retail-export.csv',
      },
      {
        label: 'Model template',
        href: '/data/datasets/table-detective-model-template.csv',
      },
      {
        label: 'Instructor notes',
        href: '/data/guides/table-detective-instructor-notes.md',
        modal: 'markdown',
        locked: true,
      },
      {
        label: 'Answer key',
        href: '/data/datasets/table-detective-answer-key.csv',
        locked: true,
      },
    ],
    file: 'ct-08-data-tables-fields-relationships.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'ct-09-how-databases-work',
    title: 'CT 09 · How Databases Work',
    topics: ['Database engines', 'Constraints', 'OLTP vs OLAP', 'Index basics'],
    resources: [
      {
        label: 'Database concepts guide',
        ctaLabel: 'Open guide',
        href: '/data/guides/ct-09-database-concepts-guide.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'ct-09-how-databases-work.pptx',
    track: null,
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'ct-10-request-to-spec',
    title: 'CT 10 · From Request to Spec',
    topics: ['Question vs report vs system', 'Source / logic / destination', 'Pinning definitions', 'Gap triage', 'Acceptance tests'],
    resources: [
      {
        label: 'Request to spec course + graded practice',
        ctaLabel: 'Open course',
        href: '/data/guides/ct-10-request-to-spec-course.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: null,
    track: null,
    cohort: 1,
    active: true,
    date: null,
    kind: 'note',
  },
  {
    id: 'stats-01-centre-spread',
    title: 'Statistics 1 - Centre and Spread',
    topics: ['Mean / median / mode', 'Range', 'Standard deviation', 'Mean vs median'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_01a_core_centre_spread.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples, practice, and home tests',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_01a_practice_centre_spread.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-03',
    kind: 'note',
  },
  {
    id: 'stats-02-relationship',
    title: 'Statistics 2 - Relationship',
    topics: ['Rank', 'Quartiles / IQR', 'Percentiles', 'Correlation'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_01b_core_position_relationship.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples, practice, and home tests',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_01b_practice_position_relationship.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-03',
    kind: 'note',
  },
  {
    id: 'stats-03-outliers-sampling-bias',
    title: 'Statistics 3 - Outliers and Sampling Bias',
    topics: ['IQR outlier fence', 'Z-score outlier check', 'Selection bias', 'Survivorship bias', 'Non-response bias'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_03_core_outliers_sampling_bias.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_03_practice_outliers_sampling_bias.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-03',
    kind: 'note',
  },
  {
    id: 'stats-03a-position',
    title: 'Statistics 3a - Position',
    topics: ['Rank', 'Quartiles', 'Percentiles', 'IQR'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_03a_core_position.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_03a_practice_position.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-03',
    kind: 'note',
  },
  {
    id: 'stats-04-normal-distribution-zscores',
    title: 'Statistics 4 - Normal Distribution and Z-Scores',
    topics: ['Z-scores as a universal ruler', 'Fair comparison across baselines', 'Decision flags: review, escalate, confirm'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_04_core_normal_distribution_zscores.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_04_practice_normal_distribution_zscores.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-10',
    kind: 'note',
  },
  {
    id: 'stats-05-probability-empirical-rule',
    title: 'Statistics 5 - Probability and the 68-95-99.7 Rule',
    topics: ['Probability rules and denominators', 'Empirical-rule ranges and tails', 'Decision framing from probability flags'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_05_core_probability_empirical_rule.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_05_practice_probability_empirical_rule.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-10',
    kind: 'note',
  },
  {
    id: 'stats-06-hypothesis-ab-testing',
    title: 'Statistics 6 - Hypothesis Testing and A/B Testing',
    topics: ['Null vs alternative hypothesis', 'A/B testing', 'Decision framing from the t-statistic'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_06_core_hypothesis_ab_testing.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_06_practice_hypothesis_ab_testing.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-17',
    kind: 'note',
  },
  {
    id: 'stats-07-pvalues-confidence-intervals',
    title: 'Statistics 7 - P-values and Confidence Intervals',
    topics: ['P-values', 'Confidence intervals', 'Decision framing from CI and significance'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_07_core_pvalues_confidence_intervals.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_07_practice_pvalues_confidence_intervals.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-17',
    kind: 'note',
  },
  {
    id: 'stats-08a-skewness',
    title: 'Statistics 8a - Skewness and the Symmetry Check',
    topics: ['Mean vs median gap', "Pearson's SK2", 'Right vs left skew'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_08a_core_skewness.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_08a_practice_skewness.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-24',
    kind: 'note',
  },
  {
    id: 'stats-08b-kurtosis',
    title: 'Statistics 8b - Kurtosis and Tail Weight',
    topics: ['Excess kurtosis', 'Heavy vs light tails', 'When normal rules break'],
    resources: [
      {
        label: 'Core concept',
        ctaLabel: 'Open core concept',
        href: '/data/guides/statistics_clinic_08b_core_kurtosis.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Solved examples and practice cases',
        ctaLabel: 'Open examples/practice',
        href: '/data/guides/statistics_clinic_08b_practice_kurtosis.md',
        modal: 'markdown',
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: '2026-08-24',
    kind: 'note',
  },
  {
    id: 'da-sql-capstone-business-analysis',
    title: 'SQL Capstone · Business Analysis Challenge',
    topics: ['Joins & aggregation', 'CTEs & fan-out', 'Business recommendations', 'Interview practice'],
    resources: [
      {
        label: 'SQL capstone workbook',
        ctaLabel: 'Open capstone',
        href: '/data/guides/sql-capstone-workbook.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Download SSMS setup script',
        href: '/data/guides/sql-capstone-01-setup.sql',
      },
      {
        label: 'Download model solutions',
        href: '/data/guides/sql-capstone-03-solutions.sql',
        locked: true,
      },
    ],
    file: null,
    track: 'data-analyst-job-ready',
    cohort: 1,
    active: true,
    date: null,
    kind: 'note',
  },
  // ── SQL Track ─────────────────────────────────────────────────────────────
  {
    id: 'sql-01-introduction-to-sql',
    title: 'SQL 01 · Introduction to SQL',
    topics: ['SCQA method', 'SQL dialects', 'Tables / rows / columns', 'Practice habits'],
    resources: [
      {
        label: 'SCQA SQL question guide',
        ctaLabel: 'Open SCQA guide',
        href: '/data/guides/sql-01-introduction-scqa-guide.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'sql-01-introduction-to-sql.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-01-data-modelling-masterclass',
    title: 'SQL 02 · Data Modelling — The Five Phases',
    topics: ['The five phases', '1 Frame', '2 Read', '3 Structure', '4 Shape', '5 Prove', 'Phase gates & checklists'],
    resources: [
      {
        label: 'Data Modelling — the five phases, with graded practice',
        ctaLabel: 'Open course',
        href: '/data/guides/sql-02-data-modelling-masterclass-course.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Northstar retail export (worked dataset)',
        href: '/data/datasets/table-detective-retail-export.csv',
      },
    ],
    file: 'data-modelling-masterclass.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'dm-05-kimball-fact-tables',
    title: 'DM 05 · Kimball I — The Four Steps & Three Fact Tables',
    topics: ['Phase 4 · Shape', '4-step design process', 'Transaction / snapshot / accumulating', 'Semi-additive measures', 'Sector: healthcare'],
    resources: [
      {
        label: 'Kimball fact tables — course + graded practice', ctaLabel: 'Open course',
        href: '/data/guides/dm-05-kimball-fact-tables.md', modal: 'markdown', cardCta: true
      },
      { label: 'Hospital encounters dataset', href: '/data/datasets/dm05-hospital-encounters.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'dm-06-conformed-dimensions',
    title: 'DM 06 · Kimball II — Conformed Dimensions & the Bus Matrix',
    topics: ['Phase 4 · Shape', 'Conformed dimensions', 'Enterprise bus matrix', 'Drill-across', 'Sector: retail banking'],
    resources: [
      {
        label: 'Conformed dimensions — course', ctaLabel: 'Open course',
        href: '/data/guides/dm-06-conformed-dimensions.md', modal: 'markdown', cardCta: true
      },
      { label: 'Retail banking events dataset', href: '/data/datasets/dm06-banking-events.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'dm-07-slowly-changing-dimensions',
    title: 'DM 07 · Slowly Changing Dimensions, Types 0 to 6',
    topics: ['Phase 4 · Shape', 'SCD types 0-6', 'Surrogate keys', 'IS DISTINCT FROM', 'Sector: insurance'],
    resources: [
      {
        label: 'Slowly changing dimensions — course', ctaLabel: 'Open course',
        href: '/data/guides/dm-07-slowly-changing-dimensions.md', modal: 'markdown', cardCta: true
      },
      { label: 'Insurance policies dataset', href: '/data/datasets/dm07-insurance-policies.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'dm-08-data-vault-foundations',
    title: 'DM 08 · Data Vault 2.0 I — Hubs, Links, Satellites',
    topics: ['Phase 4 · Shape', 'Business keys', 'Hash keys', 'Hubs / links / satellites', 'Sector: logistics'],
    resources: [
      {
        label: 'Data Vault foundations — course + graded practice', ctaLabel: 'Open course',
        href: '/data/guides/dm-08-data-vault-foundations.md', modal: 'markdown', cardCta: true
      },
      { label: 'Carrier shipments dataset', href: '/data/datasets/dm08-carrier-shipments.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'dm-09-data-vault-delivery',
    title: 'DM 09 · Data Vault 2.0 II — Raw Vault, Business Vault, PIT',
    topics: ['Phase 4 · Shape', 'Raw vs business vault', 'Hard vs soft rules', 'PIT & bridge tables', 'Sector: telecoms'],
    resources: [
      {
        label: 'Data Vault delivery — course', ctaLabel: 'Open course',
        href: '/data/guides/dm-09-data-vault-delivery.md', modal: 'markdown', cardCta: true
      },
      { label: 'Telecom subscribers dataset', href: '/data/datasets/dm09-telecom-subscribers.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'dm-10-choosing-your-approach',
    title: 'DM 10 · Choosing & Combining — Inmon, Kimball, Data Vault',
    topics: ['Phase 4 · Shape', 'Inmon vs Kimball vs Vault', 'Layered architecture', 'Weighted measures', 'Sector: higher education'],
    resources: [
      {
        label: 'Choosing your approach — course', ctaLabel: 'Open course',
        href: '/data/guides/dm-10-choosing-your-approach.md', modal: 'markdown', cardCta: true
      },
      { label: 'University enrolments dataset', href: '/data/datasets/dm10-university-enrolments.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'db-01-database-fundamentals',
    title: 'DB 01 · Database Fundamentals — The Stack, Objects & Programmability',
    topics: ['DB engine / DBMS / RDBMS', 'Schemas', 'Views vs materialized views', 'Indexes', 'Constraints & transactions', 'Procedures, functions, triggers'],
    resources: [
      {
        label: 'Database fundamentals — course + graded practice', ctaLabel: 'Open course',
        href: '/data/guides/sql-03-database-fundamentals-guide.md', modal: 'markdown', cardCta: true
      },
      { label: 'Northstar retail export (worked dataset)', href: '/data/datasets/table-detective-retail-export.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'db-02-oltp-vs-olap',
    title: 'DB 02 · OLTP vs OLAP — Two Jobs, Two Shapes',
    topics: ['Write- vs read-optimized', 'ACID', 'Normalization', 'Star vs snowflake', 'SCD', 'Row-store vs columnstore', 'ETL/ELT & Synapse distribution'],
    resources: [
      {
        label: 'OLTP vs OLAP — course + graded practice', ctaLabel: 'Open course',
        href: '/data/guides/sql-04-oltp-olap-guide.md', modal: 'markdown', cardCta: true
      },
      { label: 'Northstar retail export (worked dataset)', href: '/data/datasets/table-detective-retail-export.csv' },
    ],
    file: null, track: 'sql-job-ready', cohort: 1, active: true, date: null, kind: 'note',
  },
  {
    id: 'sql-02-relational-model',
    title: 'SQL 03 · The Relational Model',
    topics: ['Normalization', 'ER diagrams', 'Fact & dimension tables', 'Star schema'],
    resources: [
      {
        label: 'Relational model guide',
        ctaLabel: 'Open guide',
        href: '/data/guides/sql-01-relational-model-guide.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'sql-01-relational-model.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-03-how-select-works',
    title: 'SQL 04 · How SELECT Works',
    topics: ['Logical execution order', 'WHERE vs HAVING', 'Common mistakes'],
    file: 'sql-02-how-select-works.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-04-joins',
    title: 'SQL 05 · JOINs Visualised',
    topics: ['INNER / LEFT / RIGHT / FULL', 'Self-join', 'Multiple joins'],
    resources: [
      {
        label: 'SQL relationships guide',
        ctaLabel: 'Open guide',
        href: '/data/guides/sql-05-relationships-guide.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'sql-03-joins.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-05-aggregation',
    title: 'SQL 06 · Aggregation & GROUP BY',
    topics: ['COUNT / SUM / AVG / MIN / MAX', 'GROUP BY & HAVING', 'NULLs in aggregates'],
    file: 'sql-04-aggregation.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-06-ctes',
    title: 'SQL 07 · Subqueries vs CTEs',
    topics: ['Inline subqueries', 'WITH clause', 'When to use each'],
    file: 'sql-05-ctes.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-07-window-functions',
    title: 'SQL 08 · Window Functions',
    topics: ['OVER / PARTITION BY', 'ROW_NUMBER / RANK / DENSE_RANK', 'LAG / LEAD'],
    file: 'sql-06-window-functions.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-08-indexes',
    title: 'SQL 09 · Indexes & Query Performance',
    topics: ['B-tree index', 'EXPLAIN ANALYZE', 'Common performance killers'],
    resources: [
      {
        label: 'Indexing & partitioning guide',
        ctaLabel: 'Open guide',
        href: '/data/guides/sql-06-indexing-partitioning-guide.md',
        modal: 'markdown',
        cardCta: true,
      },
    ],
    file: 'sql-07-indexes.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-09-filtering-sorting-null-logic-deep-dive',
    title: 'SQL 10 · Filtering, Sorting & NULL Logic Deep Dive',
    topics: ['Boolean logic', 'NULL behaviour', 'ORDER BY', 'Top-N queries'],
    file: 'sql-09-filtering-sorting-null-logic-deep-dive.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-10-case-when-conditional-aggregation',
    title: 'SQL 11 · CASE WHEN & Conditional Aggregation',
    topics: ['CASE labels', 'SUM(CASE)', 'Segment bands', 'Business rules'],
    file: 'sql-10-case-when-conditional-aggregation.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-11-date-time-analysis',
    title: 'SQL 12 · Date and Time Analysis',
    topics: ['Periods', 'Recency', 'Date boundaries', 'Rolling windows'],
    file: 'sql-11-date-time-analysis.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-12-text-cleaning-in-sql',
    title: 'SQL 13 · Text Cleaning in SQL',
    topics: ['TRIM/LOWER', 'REPLACE', 'LIKE', 'Mapping tables'],
    file: 'sql-12-text-cleaning-in-sql.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-13-data-quality-checks-in-sql',
    title: 'SQL 14 · Data Quality Checks in SQL',
    topics: ['Row counts', 'Null checks', 'Uniqueness', 'Range checks'],
    file: 'sql-13-data-quality-checks-in-sql.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-14-duplicate-detection-deduplication',
    title: 'SQL 15 · Duplicate Detection & Deduplication',
    topics: ['GROUP BY HAVING', 'ROW_NUMBER', 'Keeper rules', 'Audit output'],
    file: 'sql-14-duplicate-detection-deduplication.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-15-advanced-join-patterns',
    title: 'SQL 16 · Advanced JOIN Patterns',
    topics: ['Anti-joins', 'Semi-joins', 'Bridge tables', 'Fan-out checks'],
    file: 'sql-15-advanced-join-patterns.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'JOIN patterns take-home', ctaLabel: 'Open exercise', href: '/data/guides/sql-15-advanced-join-patterns-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail order lines', href: '/data/datasets/retail-orders-lines.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Retail stores', href: '/data/datasets/retail-stores.csv' },
      { label: 'Instructor notes', href: '/data/guides/sql-15-advanced-join-patterns-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'sql-16-window-functions-deep-dive',
    title: 'SQL 17 · Window Functions Deep Dive',
    topics: ['Running totals', 'LAG/LEAD', 'Window frames', 'Retention patterns'],
    file: 'sql-16-window-functions-deep-dive.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'Core concepts', href: '/data/guides/sql-16b-window-functions.md', modal: 'markdown', locked: false }, { label: 'Window functions take-home', ctaLabel: 'Open exercise', href: '/data/guides/sql-16-window-functions-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail order lines', href: '/data/datasets/retail-orders-lines.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Instructor notes', href: '/data/guides/sql-16-window-functions-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'sql-17-cte-chains-analysis',
    title: 'SQL 18 · CTE Chains for Analysis',
    topics: ['Base CTEs', 'Clean stages', 'Metric stages', 'Readable final SELECT'],
    file: 'sql-17-cte-chains-analysis.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-18-views-materialized-views',
    title: 'SQL 19 · Views and Materialized Views',
    topics: ['Reporting views', 'Materialization', 'Freshness', 'Security views'],
    file: 'sql-18-views-materialized-views.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-19-schema-design-project',
    title: 'SQL 20 · Schema Design Project',
    topics: ['DDL', 'PK/FK', 'Constraints', 'ERD evidence'],
    file: 'sql-19-schema-design-project.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'Schema design project', ctaLabel: 'Open project', href: '/data/guides/sql-19-schema-design-project-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail order lines', href: '/data/datasets/retail-orders-lines.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Retail stores', href: '/data/datasets/retail-stores.csv' },
      { label: 'Instructor notes', href: '/data/guides/sql-19-schema-design-project-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'sql-20-constraints-transactions-acid',
    title: 'SQL 21 · Constraints, Transactions & ACID',
    topics: ['Constraints', 'Transactions', 'Rollback', 'ACID'],
    file: 'sql-20-constraints-transactions-acid.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-21-query-performance-case-study',
    title: 'SQL 22 · Query Performance Case Study',
    topics: ['EXPLAIN', 'Indexes', 'Selectivity', 'Before/after evidence'],
    file: 'sql-21-query-performance-case-study.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-22-sql-admin-basics',
    title: 'SQL 23 · SQL Admin Basics',
    topics: ['Roles', 'Grants', 'Backup/restore', 'Runbooks'],
    file: 'sql-22-sql-admin-basics.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'sql-23-sql-portfolio-project',
    title: 'SQL 24 · SQL Portfolio Project',
    topics: ['README', 'ERD', 'Quality checks', 'Insight summary'],
    file: 'sql-23-sql-portfolio-project.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'SQL portfolio project', ctaLabel: 'Open project', href: '/data/guides/sql-23-sql-portfolio-project-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail order lines', href: '/data/datasets/retail-orders-lines.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Retail stores', href: '/data/datasets/retail-stores.csv' },
      { label: 'Instructor notes', href: '/data/guides/sql-23-sql-portfolio-project-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'sql-24-sql-interview-scenario-drills',
    title: 'SQL 25 · SQL Interview Scenario Drills',
    topics: ['Prompt parsing', 'Pattern choice', 'Explain aloud', 'Review log'],
    file: 'sql-24-sql-interview-scenario-drills.pptx',
    track: 'sql-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },

  // ── Python Track ──────────────────────────────────────────────────────────
  {
    id: 'py-01-python-vs-excel',
    title: 'PY 01 · Python vs Excel',
    topics: ['Why Python?', 'The automation payoff', "Python's data ecosystem"],
    file: 'py-01-python-vs-excel.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-02-data-types',
    title: 'PY 02 · Data Types & Structures',
    topics: ['Scalars: int, float, str, bool', 'Lists, dicts, tuples, sets', 'When to use each'],
    file: 'py-02-data-types.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-03-functions-scope',
    title: 'PY 03 · Functions & Scope',
    topics: ['DRY principle', 'Parameters, defaults & return', 'LEGB scope rule'],
    file: 'py-03-functions-scope.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-04-pandas',
    title: 'PY 04 · Pandas Mental Model',
    topics: ['DataFrame & Series', 'Selecting, filtering & .loc', 'GroupBy & merge'],
    file: 'py-04-pandas.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-05-control-flow',
    title: 'PY 05 · Control Flow',
    topics: ['if / elif / else', 'for & while loops', 'List comprehensions'],
    file: 'py-05-control-flow.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-06-tracebacks',
    title: 'PY 06 · Reading Tracebacks',
    topics: ['Common error types', 'The debugging loop', 'try / except'],
    file: 'py-06-tracebacks.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-07-file-io',
    title: 'PY 07 · File I/O & Automation',
    topics: ['Reading & writing files', 'pathlib for paths', 'Batch processing CSVs'],
    file: 'py-07-file-io.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-08-python-project-structure',
    title: 'PY 08 · Python Project Structure',
    topics: ['src layout', 'Config', 'README runbook', 'Reusable modules'],
    file: 'py-08-python-project-structure.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-09-working-with-apis',
    title: 'PY 09 · Working with APIs',
    topics: ['Requests', 'Auth & status codes', 'Pagination', 'JSON normalization'],
    file: 'py-09-working-with-apis.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-10-pandas-cleaning-deep-dive',
    title: 'PY 10 · Pandas Cleaning Deep Dive',
    topics: ['Types & dates', 'Nulls & duplicates', 'Text cleanup', 'Audit checks'],
    file: 'py-10-pandas-cleaning-deep-dive.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'Pandas cleaning take-home', ctaLabel: 'Open exercise', href: '/data/guides/py-10-pandas-cleaning-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Messy retail orders', href: '/data/datasets/messy-retail-orders.csv' },
      { label: 'Instructor notes', href: '/data/guides/py-10-pandas-cleaning-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'py-11-pandas-joins-groupby-reshape',
    title: 'PY 11 · Pandas Joins, GroupBy & Reshape',
    topics: ['merge validation', 'Join audits', 'groupby.agg', 'melt & pivot'],
    file: 'py-11-pandas-joins-groupby-reshape.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'Pandas join take-home', ctaLabel: 'Open exercise', href: '/data/guides/py-11-pandas-joins-groupby-reshape-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail order lines', href: '/data/datasets/retail-orders-lines.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Retail stores', href: '/data/datasets/retail-stores.csv' },
      { label: 'Instructor notes', href: '/data/guides/py-11-pandas-joins-groupby-reshape-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'py-12-python-functions-data-pipelines',
    title: 'PY 12 · Python Functions for Data Pipelines',
    topics: ['extract/transform/load', 'Parameters', 'Validation', 'Composable main flow'],
    file: 'py-12-python-functions-data-pipelines.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-13-error-handling-logging-config',
    title: 'PY 13 · Error Handling, Logging & Config',
    topics: ['Exceptions', 'Logging', 'Config/env vars', 'Run summaries'],
    file: 'py-13-error-handling-logging-config.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-14-testing-python-data-code',
    title: 'PY 14 · Testing Python Data Code',
    topics: ['pytest mindset', 'Fixtures', 'Schema checks', 'Edge cases'],
    file: 'py-14-testing-python-data-code.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-15-automation-scripts-cli-tools',
    title: 'PY 15 · Automation Scripts & CLI Tools',
    topics: ['argparse', 'Batch files', 'Dry-run mode', 'Exit codes'],
    file: 'py-15-automation-scripts-cli-tools.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-16-python-to-pyspark-bridge',
    title: 'PY 16 · Python to PySpark Bridge',
    topics: ['DataFrame mindset', 'Lazy evaluation', 'Avoid collect', 'Distributed writes'],
    file: 'py-16-python-to-pyspark-bridge.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-17-object-oriented-python',
    title: 'PY 17 · Object-Oriented Python',
    topics: ['Classes', 'Dataclasses', 'Methods', 'Composition'],
    file: 'py-17-object-oriented-python.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-18-python-modules-packaging',
    title: 'PY 18 · Python Modules & Packaging',
    topics: ['Modules', 'Packages', '__init__.py', 'pyproject'],
    file: 'py-18-python-modules-packaging.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-19-virtual-environments-dependency-management',
    title: 'PY 19 · Virtual Environments & Dependency Management',
    topics: ['venv', 'requirements', 'Version pinning', 'Dev dependencies'],
    file: 'py-19-virtual-environments-dependency-management.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-20-working-with-databases-python',
    title: 'PY 20 · Working with Databases from Python',
    topics: ['Connections', 'Parameterized SQL', 'fetch/read_sql', 'Transactions'],
    file: 'py-20-working-with-databases-python.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-21-sqlalchemy-core-orm',
    title: 'PY 21 · SQLAlchemy Core & ORM',
    topics: ['Engine', 'Core', 'ORM models', 'Sessions'],
    file: 'py-21-sqlalchemy-core-orm.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-22-fastapi-foundations',
    title: 'PY 22 · FastAPI Foundations',
    topics: ['Routes', 'Path/query params', 'Pydantic models', 'JSON responses'],
    file: 'py-22-fastapi-foundations.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-23-fastapi-crud-database',
    title: 'PY 23 · FastAPI CRUD with Database',
    topics: ['CRUD routes', 'DB sessions', 'Status codes', 'Route tests'],
    file: 'py-23-fastapi-crud-database.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'FastAPI CRUD take-home', ctaLabel: 'Open exercise', href: '/data/guides/py-23-fastapi-crud-database-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Instructor notes', href: '/data/guides/py-23-fastapi-crud-database-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'py-24-authentication-api-security-basics',
    title: 'PY 24 · Authentication & API Security Basics',
    topics: ['Authn/Authz', 'Tokens/API keys', 'Hashing', 'Secrets'],
    file: 'py-24-authentication-api-security-basics.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-25-async-python-concurrency',
    title: 'PY 25 · Async Python & Concurrency',
    topics: ['async def', 'await', 'gather', 'Concurrency limits'],
    file: 'py-25-async-python-concurrency.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-26-performance-profiling',
    title: 'PY 26 · Performance Profiling',
    topics: ['Timing', 'cProfile', 'Vectorization', 'Caching'],
    file: 'py-26-performance-profiling.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-27-docker-python-projects',
    title: 'PY 27 · Docker for Python Projects',
    topics: ['Dockerfile', 'Images', 'Containers', 'Env/volume/port'],
    file: 'py-27-docker-python-projects.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-28-cicd-python-projects',
    title: 'PY 28 · CI/CD for Python Projects',
    topics: ['GitHub Actions', 'Tests', 'Lint/build', 'Secrets'],
    file: 'py-28-cicd-python-projects.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-29-python-capstone-api-database',
    title: 'PY 29 · Python Capstone: API + Database',
    topics: ['Resource model', 'Database schema', 'Tests', 'README evidence'],
    file: 'py-29-python-capstone-api-database.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'py-30-python-capstone-data-automation-pipeline',
    title: 'PY 30 · Python Capstone: Data Automation Pipeline',
    topics: ['Ingest', 'Transform', 'Validate', 'Operate'],
    file: 'py-30-python-capstone-data-automation-pipeline.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'Automation pipeline capstone', ctaLabel: 'Open project', href: '/data/guides/py-30-data-automation-pipeline-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Messy retail orders', href: '/data/datasets/messy-retail-orders.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Instructor notes', href: '/data/guides/py-30-data-automation-pipeline-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'py-31-python-interview-debugging-drills',
    title: 'PY 31 · Python Interview & Debugging Drills',
    topics: ['Trace code', 'Debug hypotheses', 'Data structures', 'Complexity notes'],
    file: 'py-31-python-interview-debugging-drills.pptx',
    track: 'python-job-ready',
    cohort: 1,
    active: true,
    date: null,
  },

  // ── Azure Data Engineer Track ─────────────────────────────────────────────
  {
    id: 'az-01-cloud-vs-on-prem',
    title: 'AZ 01 · Cloud vs On-Premise',
    topics: ['IaaS / PaaS / SaaS', 'Azure regions & availability zones', 'Shared responsibility model', 'Parallel: GCP + Terraform foundations'],
    resources: [
      {
        label: 'AZ 01 take-home · Northstar cloud decision brief',
        ctaLabel: 'Open AZ 01 DIY',
        href: '/data/guides/az-01-cloud-vs-on-prem-take-home.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Azure domain-expert mastery standard',
        ctaLabel: 'Open mastery standard',
        href: '/data/guides/azure-domain-expert-mastery-standard.md',
        modal: 'markdown',
        cardCta: true,
      },
      {
        label: 'Credit · DataTalksClub Data Engineering Zoomcamp materials',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp',
      },
      {
        label: 'DataTalksClub Zoomcamp Module 1: Containerization and IaC',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/01-docker-terraform',
      },
    ],
    file: 'az-01-cloud-vs-on-prem.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-02-azure-storage',
    title: 'AZ 02 · Azure Storage',
    topics: ['Blob Storage & containers', 'ADLS Gen2', 'Access tiers & replication', 'Parallel: data lakes + orchestration'],
    resources: [
      {
        label: 'Credit · DataTalksClub Data Engineering Zoomcamp materials',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp',
      },
      {
        label: 'DataTalksClub Zoomcamp Module 2: Workflow Orchestration',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/02-workflow-orchestration',
      },
    ],
    file: 'az-02-azure-storage.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-03-data-factory',
    title: 'AZ 03 · Azure Data Factory',
    topics: ['Pipelines & activities', 'Watermarks & retries', 'Metadata-driven ADF', 'Parallel: API ingestion + incremental loading'],
    resources: [
      {
        label: 'Credit · DataTalksClub Data Engineering Zoomcamp materials',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp',
      },
      {
        label: 'DataTalksClub Zoomcamp Workshop 1: Data Ingestion',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp#workshop-1-data-ingestion',
      },
    ],
    file: 'az-03-data-factory.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-04-medallion',
    title: 'AZ 04 · Medallion Architecture',
    topics: ['Bronze / Silver / Gold layers', 'Delta Lake & ACID', 'dbt-style marts & tests', 'Parallel: warehousing + analytics engineering'],
    resources: [
      {
        label: 'Credit · DataTalksClub Data Engineering Zoomcamp materials',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp',
      },
      {
        label: 'DataTalksClub Zoomcamp Module 3: Data Warehousing',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/03-data-warehouse',
      },
      {
        label: 'DataTalksClub Zoomcamp Module 4: Analytics Engineering',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/04-analytics-engineering',
      },
      {
        label: 'dbt Docs · Analytics Engineering',
        href: 'https://docs.getdbt.com/docs/introduction',
      },
    ],
    file: 'az-04-medallion.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-05-spark-vs-sql',
    title: 'AZ 05 · Spark vs SQL',
    topics: ['Distributed computing basics', 'Lazy evaluation', 'Performance tradeoffs', 'Parallel: Bruin + Spark batch processing'],
    resources: [
      {
        label: 'Credit · DataTalksClub Data Engineering Zoomcamp materials',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp',
      },
      {
        label: 'DataTalksClub Zoomcamp Module 5: Data Platforms',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/05-data-platforms',
      },
      {
        label: 'DataTalksClub Zoomcamp Module 6: Batch Processing',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/06-batch',
      },
      {
        label: 'Snowflake Docs · Warehouse comparison',
        href: 'https://docs.snowflake.com/',
      },
    ],
    file: 'az-05-spark-vs-sql.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-06-databricks',
    title: 'AZ 06 · Databricks',
    topics: ['Workspace, clusters & notebooks', 'Delta Lake on Databricks', 'Unity Catalog', 'Production jobs & CI/CD'],
    resources: [
      {
        label: 'Credit · DataTalksClub Data Engineering Zoomcamp materials',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp',
      },
      {
        label: 'DataTalksClub Zoomcamp Module 7: Streaming',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/07-streaming',
      },
    ],
    file: 'az-06-databricks.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-07-security',
    title: 'AZ 07 · Security & Access Control',
    topics: ['RBAC & least privilege', 'Managed Identity', 'Key Vault', 'Purview, lineage & governance'],
    resources: [
      {
        label: 'Credit · DataTalksClub Data Engineering Zoomcamp materials',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp',
      },
      {
        label: 'DataTalksClub Zoomcamp Final Project',
        href: 'https://github.com/DataTalksClub/data-engineering-zoomcamp/tree/main/projects',
      },
      {
        label: 'Microsoft Purview docs',
        href: 'https://learn.microsoft.com/en-us/purview/',
      },
    ],
    file: 'az-07-security.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-08-microsoft-fabric-big-picture',
    title: 'AZ 08 · Microsoft Fabric Big Picture',
    topics: ['Fabric SaaS platform', 'OneLake', 'Workloads', 'Capacity & governance'],
    file: 'az-08-microsoft-fabric-big-picture.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-09-onelake-lakehouse-architecture',
    title: 'AZ 09 · OneLake & Lakehouse Architecture',
    topics: ['OneLake hierarchy', 'Lakehouse files & tables', 'Shortcuts', 'SQL endpoint'],
    file: 'az-09-onelake-lakehouse-architecture.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-10-fabric-experiences-explained',
    title: 'AZ 10 · Fabric Experiences Explained',
    topics: ['Data Factory', 'Data Engineering', 'Warehouse', 'Real-Time Intelligence'],
    file: 'az-10-fabric-experiences-explained.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-11-fabric-workspace-capacity-admin',
    title: 'AZ 11 · Fabric Workspace, Capacity & Admin',
    topics: ['Tenant settings', 'Capacity', 'Workspace roles', 'Domains'],
    file: 'az-11-fabric-workspace-capacity-admin.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-12-fabric-lakehouse',
    title: 'AZ 12 · Fabric Lakehouse',
    topics: ['Files vs tables', 'Delta tables', 'SQL analytics endpoint', 'Semantic models'],
    file: 'az-12-fabric-lakehouse.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-13-fabric-notebooks-spark',
    title: 'AZ 13 · Fabric Notebooks & Spark',
    topics: ['PySpark notebooks', 'Lakehouse attachment', 'Environments', 'Scheduled jobs'],
    file: 'az-13-fabric-notebooks-spark.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-14-fabric-data-factory',
    title: 'AZ 14 · Fabric Data Factory',
    topics: ['Pipelines', 'Dataflows Gen2', 'Copy activity', 'Triggers & parameters'],
    file: 'az-14-fabric-data-factory.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-15-fabric-warehouse',
    title: 'AZ 15 · Fabric Warehouse',
    topics: ['Warehouse vs Lakehouse', 'T-SQL', 'Dimensional marts', 'Performance'],
    file: 'az-15-fabric-warehouse.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-16-fabric-mirroring-shortcuts',
    title: 'AZ 16 · Fabric Mirroring & Shortcuts',
    topics: ['Mirroring', 'OneLake shortcuts', 'Zero-copy access', 'Lineage risks'],
    file: 'az-16-fabric-mirroring-shortcuts.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-17-fabric-real-time-intelligence',
    title: 'AZ 17 · Fabric Real-Time Intelligence',
    topics: ['Eventstreams', 'Eventhouse', 'KQL', 'Windowing'],
    file: 'az-17-fabric-real-time-intelligence.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-18-incremental-loading-patterns',
    title: 'AZ 18 · Incremental Loading Patterns',
    topics: ['Full vs incremental', 'Watermarks', 'CDC', 'Idempotent reruns'],
    file: 'az-18-incremental-loading-patterns.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-19-data-quality-azure-pipelines',
    title: 'AZ 19 · Data Quality in Azure Pipelines',
    topics: ['Validation', 'Quarantine', 'Reconciliation', 'Schema drift'],
    file: 'az-19-data-quality-azure-pipelines.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-20-delta-lake-deep-dive',
    title: 'AZ 20 · Delta Lake Deep Dive',
    topics: ['Transaction log', 'ACID', 'Time travel', 'Optimize & vacuum'],
    file: 'az-20-delta-lake-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-21-lakehouse-data-modelling',
    title: 'AZ 21 · Lakehouse Data Modelling',
    topics: ['Facts & dimensions', 'SCDs', 'Gold marts', 'Semantic layer'],
    file: 'az-21-lakehouse-data-modelling.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-22-azure-landing-zone-data-platforms',
    title: 'AZ 22 · Azure Landing Zone for Data Platforms',
    topics: ['Subscriptions', 'Resource groups', 'Naming & tags', 'Policy'],
    file: 'az-22-azure-landing-zone-data-platforms.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-23-networking-for-data-engineers',
    title: 'AZ 23 · Networking for Data Engineers',
    topics: ['VNets', 'Private endpoints', 'Firewalls', 'Integration runtime'],
    file: 'az-23-networking-for-data-engineers.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-24-identity-secrets-production',
    title: 'AZ 24 · Identity & Secrets in Production',
    topics: ['Managed Identity', 'Service principals', 'Key Vault', 'Least privilege'],
    file: 'az-24-identity-secrets-production.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-25-infrastructure-as-code',
    title: 'AZ 25 · Infrastructure as Code',
    topics: ['Terraform/Bicep', 'State', 'Modules', 'Drift control'],
    file: 'az-25-infrastructure-as-code.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-26-cicd-data-platforms',
    title: 'AZ 26 · CI/CD for Data Platforms',
    topics: ['Git integration', 'Deployment pipelines', 'Tests', 'Environment promotion'],
    file: 'az-26-cicd-data-platforms.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-27-monitoring-logging-alerting',
    title: 'AZ 27 · Monitoring, Logging & Alerting',
    topics: ['Metrics', 'Logs', 'Alerts', 'Runbooks'],
    file: 'az-27-monitoring-logging-alerting.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-28-cost-management-finops',
    title: 'AZ 28 · Cost Management & FinOps',
    topics: ['Capacity cost', 'Compute', 'Storage', 'Budgets & tags'],
    file: 'az-28-cost-management-finops.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-29-governance-with-purview',
    title: 'AZ 29 · Governance with Purview',
    topics: ['Catalog', 'Lineage', 'Sensitivity labels', 'Endorsement'],
    file: 'az-29-governance-with-purview.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-30-batch-architecture-case-study',
    title: 'AZ 30 · Batch Architecture Case Study',
    topics: ['Daily pipeline', 'Bronze/Silver/Gold', 'Serving', 'Recovery'],
    file: 'az-30-batch-architecture-case-study.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-31-streaming-architecture-case-study',
    title: 'AZ 31 · Streaming Architecture Case Study',
    topics: ['Events', 'Eventstreams', 'Processing', 'Alerts'],
    file: 'az-31-streaming-architecture-case-study.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-32-migration-to-fabric',
    title: 'AZ 32 · Migration to Fabric',
    topics: ['Assessment', 'Coexistence', 'Validation', 'Cutover'],
    file: 'az-32-migration-to-fabric.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-33-azure-data-engineer-system-design',
    title: 'AZ 33 · Azure Data Engineer System Design',
    topics: ['Clarify requirements', 'Architecture tradeoffs', 'Failure modes', 'Cost & security'],
    file: 'az-33-azure-data-engineer-system-design.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-34-spark-dataframe-transformations',
    title: 'AZ 34 · Spark DataFrame Transformations',
    topics: ['select/filter', 'withColumn', 'groupBy.agg', 'Delta writes'],
    file: 'az-34-spark-dataframe-transformations.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-35-spark-joins-shuffles-partitioning',
    title: 'AZ 35 · Spark Joins, Shuffles & Partitioning',
    topics: ['Shuffles', 'Broadcast joins', 'Skew', 'Partition design'],
    file: 'az-35-spark-joins-shuffles-partitioning.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-36-spark-performance-debugging',
    title: 'AZ 36 · Spark Performance Debugging',
    topics: ['Explain plans', 'Spark UI', 'Spill', 'Small files'],
    file: 'az-36-spark-performance-debugging.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-37-fabric-notebook-production-patterns',
    title: 'AZ 37 · Fabric Notebook Production Patterns',
    topics: ['Parameters', 'Idempotent writes', 'Quality gates', 'Run logs'],
    file: 'az-37-fabric-notebook-production-patterns.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-38-pyspark-etl-project-walkthrough',
    title: 'AZ 38 · PySpark ETL Project Walkthrough',
    topics: ['Bronze', 'Silver', 'Gold', 'Portfolio evidence'],
    file: 'az-38-pyspark-etl-project-walkthrough.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'PySpark ETL take-home', ctaLabel: 'Open exercise', href: '/data/guides/az-38-pyspark-etl-project-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail order lines', href: '/data/datasets/retail-orders-lines.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Retail stores', href: '/data/datasets/retail-stores.csv' },
      { label: 'Instructor notes', href: '/data/guides/az-38-pyspark-etl-project-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'az-39-spark-structured-streaming-deep-dive',
    title: 'AZ 39 · Spark Structured Streaming Deep Dive',
    topics: ['readStream', 'Checkpoints', 'Watermarks', 'Output modes'],
    file: 'az-39-spark-structured-streaming-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-40-databricks-jobs-workflows',
    title: 'AZ 40 · Databricks Jobs & Workflows',
    topics: ['Task graphs', 'Job clusters', 'Parameters', 'Repair runs'],
    file: 'az-40-databricks-jobs-workflows.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-41-databricks-cluster-cost-performance',
    title: 'AZ 41 · Databricks Cluster Cost & Performance',
    topics: ['Autoscaling', 'Job clusters', 'Runtimes', 'Cost tradeoffs'],
    file: 'az-41-databricks-cluster-cost-performance.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-42-delta-lake-databricks-deep-dive',
    title: 'AZ 42 · Delta Lake on Databricks Deep Dive',
    topics: ['MERGE', 'History', 'Optimize/ZORDER', 'Vacuum'],
    file: 'az-42-delta-lake-databricks-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-43-unity-catalog-deep-dive',
    title: 'AZ 43 · Unity Catalog Deep Dive',
    topics: ['Catalogs', 'External locations', 'Grants', 'Lineage'],
    file: 'az-43-unity-catalog-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-44-databricks-cicd-asset-bundles',
    title: 'AZ 44 · Databricks CI/CD & Asset Bundles',
    topics: ['Bundles', 'Targets', 'Validation', 'Deploy/run'],
    file: 'az-44-databricks-cicd-asset-bundles.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-45-fabric-dataflows-gen2-deep-dive',
    title: 'AZ 45 · Fabric Dataflows Gen2 Deep Dive',
    topics: ['Connectors', 'Power Query', 'Destinations', 'Refresh errors'],
    file: 'az-45-fabric-dataflows-gen2-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-46-fabric-pipeline-patterns',
    title: 'AZ 46 · Fabric Pipeline Patterns',
    topics: ['Metadata-driven', 'ForEach', 'Parameters', 'Failure branches'],
    file: 'az-46-fabric-pipeline-patterns.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'Fabric pipeline take-home', ctaLabel: 'Open exercise', href: '/data/guides/az-46-fabric-pipeline-patterns-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Retail order lines', href: '/data/datasets/retail-orders-lines.csv' },
      { label: 'Retail customers', href: '/data/datasets/retail-customers.csv' },
      { label: 'Retail products', href: '/data/datasets/retail-products.csv' },
      { label: 'Instructor notes', href: '/data/guides/az-46-fabric-pipeline-patterns-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
  {
    id: 'az-47-fabric-warehouse-tsql-deep-dive',
    title: 'AZ 47 · Fabric Warehouse T-SQL Deep Dive',
    topics: ['CTAS', 'Views', 'Dim/fact marts', 'Query tuning'],
    file: 'az-47-fabric-warehouse-tsql-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-48-fabric-eventstreams-kql-deep-dive',
    title: 'AZ 48 · Fabric Eventstreams & KQL Deep Dive',
    topics: ['Eventstreams', 'Eventhouse', 'KQL summarize', 'Alerts'],
    file: 'az-48-fabric-eventstreams-kql-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-49-fabric-security-governance-deep-dive',
    title: 'AZ 49 · Fabric Security & Governance Deep Dive',
    topics: ['Workspace roles', 'RLS/CLS/OLS', 'Labels', 'Audit logs'],
    file: 'az-49-fabric-security-governance-deep-dive.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
  },
  {
    id: 'az-50-dp-700-exam-scenario-drills',
    title: 'AZ 50 · DP-700 Exam Scenario Drills',
    topics: ['Tool choice', 'Loading patterns', 'Optimization', 'Governance scenarios'],
    file: 'az-50-dp-700-exam-scenario-drills.pptx',
    track: 'azure-data-engineer',
    cohort: 1,
    active: true,
    date: null,
    resources: [
      { label: 'DP-700 scenario drills', ctaLabel: 'Open drills', href: '/data/guides/az-50-dp-700-scenario-drills-exercise.md', modal: 'markdown', cardCta: true },
      { label: 'Answer key', href: '/data/datasets/dp700-scenario-drills-answer-key.csv', locked: true },
      { label: 'Instructor notes', href: '/data/guides/az-50-dp-700-scenario-drills-instructor-notes.md', modal: 'markdown', locked: true },
    ],
  },
];

const DEFAULT_TRACK_PRACTICE = {
  'sql-job-ready': {
    label: 'SQL practice take-home',
    ctaLabel: 'Open practice',
    href: '/data/guides/sql-track-practice-template.md',
    modal: 'markdown',
    cardCta: true,
  },
  'python-job-ready': {
    label: 'Python practice take-home',
    ctaLabel: 'Open practice',
    href: '/data/guides/python-track-practice-template.md',
    modal: 'markdown',
    cardCta: true,
  },
  'azure-data-engineer': {
    label: 'Azure practice take-home',
    ctaLabel: 'Open practice',
    href: '/data/guides/azure-track-practice-template.md',
    modal: 'markdown',
    cardCta: true,
  },
};

const SESSION_SLIDES_WITH_PRACTICE = SESSION_SLIDES.map((slide) => {
  if (slide.track === 'python-job-ready' && /^py-(?:0[1-9]|[12][0-9]|30)-/.test(slide.id)) {
    const href = `/data/guides/${slide.id}-take-home.md`;
    const existingResources = (slide.resources || []).map((resource) =>
      resource.cardCta && resource.href !== href
        ? { ...resource, cardCta: false }
        : resource
    );
    const hasDedicatedTakeHome = existingResources.some((resource) => resource.href === href);
    return {
      ...slide,
      resources: hasDedicatedTakeHome
        ? existingResources
        : [
          {
            label: `${slide.title} · bespoke take-home DIY`,
            ctaLabel: `Open ${slide.title.split(' · ')[0]} DIY`,
            href,
            modal: 'markdown',
            cardCta: true,
          },
          ...existingResources,
        ],
    };
  }
  if (slide.track === 'azure-data-engineer') {
    const href = `/data/guides/${slide.id}-take-home.md`;
    const existingResources = (slide.resources || []).map((resource) =>
      resource.cardCta && resource.href !== href
        ? { ...resource, cardCta: false }
        : resource
    );
    const hasDedicatedTakeHome = existingResources.some((resource) => resource.href === href);
    return {
      ...slide,
      resources: hasDedicatedTakeHome
        ? existingResources
        : [
          {
            label: `${slide.title} · bespoke take-home DIY`,
            ctaLabel: `Open ${slide.title.split(' · ')[0]} DIY`,
            href,
            modal: 'markdown',
            cardCta: true,
          },
          ...existingResources,
        ],
    };
  }
  const defaultPractice = DEFAULT_TRACK_PRACTICE[slide.track];
  if (!defaultPractice) return slide;
  if ((slide.resources || []).some((resource) => resource.cardCta)) return slide;
  return {
    ...slide,
    resources: [defaultPractice, ...(slide.resources || [])],
  };
});

export default SESSION_SLIDES_WITH_PRACTICE;
